import React, { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence, useInView, useReducedMotion } from "framer-motion";
import { IMG } from "@/lib/images";
import { DURATION, EASE } from "@/lib/motion";
import RealPhone from "@/components/RealPhone";

/**
 * StickyWalkthrough — sticky phone on the left, scrolling steps on the right.
 *
 * Phone screens with baked-in frames (welcome, lifestyle, festival) render as
 * raw imgs. Bare screen captures (interests, create) get wrapped in a real
 * iPhone 16 mockup via <RealPhone>.
 */

const STEPS = [
  { eyebrow: "01 / Get started fast", title: "Welcome to the run.",
    body: "FOTR is on the run, having fun, looking for plus ones. Who wants to join?",
    img: "appWelcome", framed: false },
  { eyebrow: "02 / Find your fun", title: "See what's happening, then just show up.",
    body: "Connect now, connect later, or browse all of it.",
    img: "appLifestyle", framed: false },
  { eyebrow: "03 / Pick your vibe", title: "Pick your interests.",
    body: "Cycling, gaming, painting — we match you with people who show up.",
    img: "appInterests", framed: true },
  { eyebrow: "04 / Create an activity", title: "Start something. Find your crew.",
    body: "Pick an activity, set the time, drop a location. Push alerts go out to people nearby who care.",
    img: "appCreate", framed: true },
  { eyebrow: "05 / They actually show up", title: "Real activities. Real seats filling up.",
    body: "Every activity gets its own page — real-time chat, RSVPs, and one-tap calendar sync. The kind of magic that turns \"maybe\" into \"see you there.\"",
    img: "appFestival", framed: false },
];

// Step block — reports activation back to parent when 55% in view
function Step({ step, index, onActivate }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.55, margin: "-15% 0px -15% 0px" });

  useEffect(() => {
    if (inView) onActivate(index);
  }, [inView, index, onActivate]);

  return (
    <div
      ref={ref}
      className="min-h-[80vh] flex flex-col justify-center py-12"
      data-testid={`walkthrough-step-${index + 1}`}
    >
      <div
        className={`text-[10px] uppercase tracking-[0.25em] font-bold mb-4 transition-colors duration-300 ${
          inView ? "text-primary" : "text-muted/70"
        }`}
      >
        {step.eyebrow}
      </div>
      <h3
        className={`font-display font-black text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight transition-colors duration-300 ${
          inView ? "text-ink" : "text-ink/60"
        }`}
      >
        {step.title}
      </h3>
      <p
        className={`mt-5 text-lg max-w-lg transition-colors duration-300 ${
          inView ? "text-muted" : "text-muted/60"
        }`}
      >
        {step.body}
      </p>
    </div>
  );
}

// Progress spine — fills downward as steps activate
function ProgressSpine({ activeIdx, total }) {
  return (
    <div
      className="absolute left-[-28px] top-0 bottom-0 w-[2px] bg-line"
      aria-hidden="true"
    >
      <motion.div
        className="absolute top-0 left-0 right-0 bg-primary origin-top"
        animate={{ scaleY: (activeIdx + 1) / total, height: "100%" }}
        style={{ transformOrigin: "top" }}
        transition={{ duration: DURATION.section, ease: EASE.softEntrance }}
      />
      {/* Step nodes */}
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={`absolute -left-[5px] w-3 h-3 rounded-full border-2 transition-all duration-300 ${
            i <= activeIdx
              ? "bg-primary border-primary scale-110"
              : "bg-surface border-line"
          }`}
          style={{ top: `${(i / (total - 1)) * 100}%`, transform: "translateY(-50%)" }}
        />
      ))}
    </div>
  );
}

// Mobile horizontal swipe-card layout
function MobileWalkthrough() {
  return (
    <div className="lg:hidden">
      <div
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-8 -mx-6 px-6 scrollbar-hide"
        data-testid="walkthrough-mobile-swipe"
      >
        {STEPS.map((step, i) => (
          <div
            key={i}
            className="shrink-0 w-[85vw] max-w-sm snap-center bg-surface-alt border border-line rounded-3xl p-6 flex flex-col"
          >
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-primary mb-3">
              {step.eyebrow}
            </div>
            <h3 className="font-display font-black text-2xl leading-tight tracking-tight">
              {step.title}
            </h3>
            <p className="mt-3 text-muted text-sm">{step.body}</p>
            <div className="mt-6 flex justify-center">
              {step.framed ? (
                <div style={{ transform: "scale(0.7)", transformOrigin: "top center" }}>
                  <RealPhone src={IMG[step.img]} alt={step.title} />
                </div>
              ) : (
                <img
                  src={IMG[step.img]}
                  alt={step.title}
                  className="max-h-[360px] w-auto drop-shadow-xl"
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function StickyWalkthrough() {
  const [activeIdx, setActiveIdx] = useState(0);
  const sectionRef = useRef(null);
  const sectionInView = useInView(sectionRef, { amount: 0.15 });
  const lastScrollTriggerRef = useRef(0);  // ms timestamp of last scroll-driven change
  const reduce = useReducedMotion();

  // Scroll-driven activation from each step's IntersectionObserver
  const onActivate = React.useCallback((i) => {
    setActiveIdx(i);
    lastScrollTriggerRef.current = Date.now();
  }, []);

  // Auto-cycle: advance every 5s when section is visible and user hasn't
  // scrolled-activated a step in the last 5s. Disabled if user prefers reduced motion.
  useEffect(() => {
    if (reduce || !sectionInView) return;
    const id = setInterval(() => {
      const sinceLastScroll = Date.now() - lastScrollTriggerRef.current;
      if (sinceLastScroll < 4800) return;  // wait one full cycle after a scroll-trigger
      setActiveIdx((i) => (i + 1) % STEPS.length);
    }, 5000);
    return () => clearInterval(id);
  }, [sectionInView, reduce]);

  return (
    <section
      ref={sectionRef}
      id="showcase"
      className="py-24 md:py-32 px-6 md:px-12 relative"
      data-testid="sticky-walkthrough"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section heading */}
        <motion.div
          className="mb-12 md:mb-16 max-w-3xl"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: DURATION.section, ease: EASE.softEntrance }}
        >
          <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold mb-4">
            The App
          </div>
          <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[0.95]">
            Built for the way friendships actually start.
          </h2>
          <p className="mt-6 text-lg text-muted max-w-2xl">
            Push alerts, a live community map, real groups — designed so showing up is the easiest part.
          </p>
        </motion.div>

        {/* Mobile: horizontal swipe layout */}
        <MobileWalkthrough />

        {/* Desktop: sticky phone + scrolling steps */}
        <div className="hidden lg:grid grid-cols-12 gap-16">
          {/* Sticky phone column */}
          <div className="col-span-5">
            <div className="sticky top-28 flex items-center justify-center">
              <motion.div
                className="relative flex items-center justify-center"
                animate={reduce ? {} : { y: [0, -4, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                data-testid="walkthrough-sticky-phone"
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={STEPS[activeIdx].img}
                    initial={{ opacity: 0, scale: 0.985 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.985 }}
                    transition={{ duration: 0.28, ease: EASE.softEntrance }}
                    className="flex items-center justify-center"
                  >
                    {STEPS[activeIdx].framed ? (
                      <RealPhone
                        src={IMG[STEPS[activeIdx].img]}
                        alt={STEPS[activeIdx].title}
                        testid={`walkthrough-phone-${STEPS[activeIdx].img}`}
                      />
                    ) : (
                      <img
                        src={IMG[STEPS[activeIdx].img]}
                        alt={STEPS[activeIdx].title}
                        className="w-full max-w-[360px] h-auto drop-shadow-2xl"
                        data-testid={`walkthrough-phone-${STEPS[activeIdx].img}`}
                      />
                    )}
                  </motion.div>
                </AnimatePresence>
              </motion.div>
            </div>
          </div>

          {/* Scrolling steps column */}
          <div className="col-span-7 relative pl-8">
            <ProgressSpine activeIdx={activeIdx} total={STEPS.length} />
            {STEPS.map((step, i) => (
              <Step key={i} step={step} index={i} onActivate={onActivate} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
